/**
 * De un codigo postal a su comunidad autonoma.
 *
 * El mercado es de producto local: un vendedor solo entrega dentro de su propia
 * comunidad (decision del usuario, 2026-10-05). Stripe Checkout sabe limitar por
 * pais pero no por provincia, asi que la direccion se pide aqui y se comprueba en
 * servidor antes de abrir el pago.
 *
 * Los dos primeros digitos de un codigo postal español son la provincia, y eso no
 * ha cambiado desde que existen: no hace falta ninguna tabla ni ninguna API.
 */

export interface Comunidad {
  codigo: string
  nombre: string
}

export const COMUNIDADES: Record<string, string> = {
  AN: "Andalucía",
  AR: "Aragón",
  AS: "Asturias",
  IB: "Illes Balears",
  CN: "Canarias",
  CB: "Cantabria",
  CL: "Castilla y León",
  CM: "Castilla-La Mancha",
  CT: "Cataluña",
  VC: "Comunitat Valenciana",
  EX: "Extremadura",
  GA: "Galicia",
  MD: "Comunidad de Madrid",
  MC: "Región de Murcia",
  NC: "Navarra",
  PV: "País Vasco",
  RI: "La Rioja",
  CE: "Ceuta",
  ML: "Melilla",
}

/** Provincia (dos primeros digitos del codigo postal) → nombre y comunidad. */
const PROVINCIAS: Record<string, { nombre: string; comunidad: string }> = {
  "01": { nombre: "Álava", comunidad: "PV" },
  "02": { nombre: "Albacete", comunidad: "CM" },
  "03": { nombre: "Alicante", comunidad: "VC" },
  "04": { nombre: "Almería", comunidad: "AN" },
  "05": { nombre: "Ávila", comunidad: "CL" },
  "06": { nombre: "Badajoz", comunidad: "EX" },
  "07": { nombre: "Illes Balears", comunidad: "IB" },
  "08": { nombre: "Barcelona", comunidad: "CT" },
  "09": { nombre: "Burgos", comunidad: "CL" },
  "10": { nombre: "Cáceres", comunidad: "EX" },
  "11": { nombre: "Cádiz", comunidad: "AN" },
  "12": { nombre: "Castellón", comunidad: "VC" },
  "13": { nombre: "Ciudad Real", comunidad: "CM" },
  "14": { nombre: "Córdoba", comunidad: "AN" },
  "15": { nombre: "A Coruña", comunidad: "GA" },
  "16": { nombre: "Cuenca", comunidad: "CM" },
  "17": { nombre: "Girona", comunidad: "CT" },
  "18": { nombre: "Granada", comunidad: "AN" },
  "19": { nombre: "Guadalajara", comunidad: "CM" },
  "20": { nombre: "Gipuzkoa", comunidad: "PV" },
  "21": { nombre: "Huelva", comunidad: "AN" },
  "22": { nombre: "Huesca", comunidad: "AR" },
  "23": { nombre: "Jaén", comunidad: "AN" },
  "24": { nombre: "León", comunidad: "CL" },
  "25": { nombre: "Lleida", comunidad: "CT" },
  "26": { nombre: "La Rioja", comunidad: "RI" },
  "27": { nombre: "Lugo", comunidad: "GA" },
  "28": { nombre: "Madrid", comunidad: "MD" },
  "29": { nombre: "Málaga", comunidad: "AN" },
  "30": { nombre: "Murcia", comunidad: "MC" },
  "31": { nombre: "Navarra", comunidad: "NC" },
  "32": { nombre: "Ourense", comunidad: "GA" },
  "33": { nombre: "Asturias", comunidad: "AS" },
  "34": { nombre: "Palencia", comunidad: "CL" },
  "35": { nombre: "Las Palmas", comunidad: "CN" },
  "36": { nombre: "Pontevedra", comunidad: "GA" },
  "37": { nombre: "Salamanca", comunidad: "CL" },
  "38": { nombre: "Santa Cruz de Tenerife", comunidad: "CN" },
  "39": { nombre: "Cantabria", comunidad: "CB" },
  "40": { nombre: "Segovia", comunidad: "CL" },
  "41": { nombre: "Sevilla", comunidad: "AN" },
  "42": { nombre: "Soria", comunidad: "CL" },
  "43": { nombre: "Tarragona", comunidad: "CT" },
  "44": { nombre: "Teruel", comunidad: "AR" },
  "45": { nombre: "Toledo", comunidad: "CM" },
  "46": { nombre: "Valencia", comunidad: "VC" },
  "47": { nombre: "Valladolid", comunidad: "CL" },
  "48": { nombre: "Bizkaia", comunidad: "PV" },
  "49": { nombre: "Zamora", comunidad: "CL" },
  "50": { nombre: "Zaragoza", comunidad: "AR" },
  "51": { nombre: "Ceuta", comunidad: "CE" },
  "52": { nombre: "Melilla", comunidad: "ML" },
}

/** El codigo postal sin espacios, o `null` si no son cinco cifras de una provincia que exista. */
export function limpiarCodigoPostal(valor: string | null | undefined): string | null {
  const texto = String(valor ?? "").replace(/\s+/g, "")
  if (!/^\d{5}$/.test(texto)) return null
  return PROVINCIAS[texto.slice(0, 2)] ? texto : null
}

export function provinciaDelCodigoPostal(valor: string | null | undefined): string | null {
  const cp = limpiarCodigoPostal(valor)
  return cp ? PROVINCIAS[cp.slice(0, 2)].nombre : null
}

/** La comunidad de un codigo postal, o `null` si el codigo no vale. */
export function comunidadDelCodigoPostal(valor: string | null | undefined): Comunidad | null {
  const cp = limpiarCodigoPostal(valor)
  if (!cp) return null
  const codigo = PROVINCIAS[cp.slice(0, 2)].comunidad
  return { codigo, nombre: COMUNIDADES[codigo] }
}

/** Las mismas, como se dicen en inglés dentro de una frase («only within the Balearic Islands»). */
export const COMUNIDADES_EN: Record<string, string> = {
  AN: "Andalusia",
  AR: "Aragon",
  AS: "Asturias",
  IB: "the Balearic Islands",
  CN: "the Canary Islands",
  CB: "Cantabria",
  CL: "Castile and León",
  CM: "Castilla-La Mancha",
  CT: "Catalonia",
  VC: "the Valencian Community",
  EX: "Extremadura",
  GA: "Galicia",
  MD: "the Community of Madrid",
  MC: "the Region of Murcia",
  NC: "Navarre",
  PV: "the Basque Country",
  RI: "La Rioja",
  CE: "Ceuta",
  ML: "Melilla",
}

export function nombreDeLaComunidad(codigo: string | null | undefined, idioma: "es" | "en" = "es"): string {
  const nombres = idioma === "en" ? COMUNIDADES_EN : COMUNIDADES
  return (codigo && nombres[codigo]) || ""
}
